/*
 * Seskupení mřížky kolekce podle kategorie dvířek (Musterfarben).
 *
 * Samotné seskupení dělá CSS `order`, které si každá karta nese z Liquidu
 * (snippet bp-collection-group). Tenhle skript řeší dvě věci, na které Liquid
 * nestačí:
 *
 *  1. DOTAŽENÍ VŠECH STRÁNEK. Liquid vidí jen jednu stránku (max 36 produktů),
 *     takže bez tohohle by se seskupilo jen prvních 36 karet a zbytek by se
 *     přiléval při scrollování — karty by skákaly do skupin nad viewportem.
 *     Proto se všechny stránky natáhnou hned přes Section Rendering API
 *     a poskládají do jedné mřížky v pořadí stránek. Uvnitř skupiny totiž
 *     rozhoduje pořadí v DOM, tedy zvolené řazení.
 *
 *  2. NADPISY SKUPIN. Liquid je vykreslí všechny skryté (netuší, které skupiny
 *     na stránce nakonec budou — s aktivním filtrem klidně jen jedna). Tady se
 *     odkryjí jen ty, ke kterým nějaká karta existuje.
 *
 * SOUBĚH S FILTREM (proč to není jen „po události spustit znovu"):
 * `shopify:collection:update` se posílá HNED při kliknutí na filtr, ale sekci
 * si téma překresluje až doběhlým requestem — v okamžiku události je v DOM
 * pořád stará mřížka i s kartami, které jsme dotáhli my. Kdybychom začali
 * rovnou, karty ze starého filtru zůstanou v mřížce vedle nových a filtrování
 * vypadá rozbitě. Proto se čeká, až se mřížka opravdu překreslí, rozdělané
 * dotahování se ruší (AbortController) a naše karty se před každým během
 * odstraní.
 */
(() => {
  const CARD_SELECTOR = 'li.product-grid__item';
  const APPENDED_ATTR = 'data-bp-appended';
  const MAX_PAGES = 20; // pojistka proti nekonečné smyčce, kdyby data-last-page lhalo
  const SETTLE_MS = 150; // ticho v mutacích, po kterém považujeme překreslení za hotové
  const SETTLE_TIMEOUT_MS = 2000; // když se mřížka nezmění vůbec (stejný výsledek)

  /** @type {AbortController | null} */
  let inFlight = null;
  /** @type {number | undefined} */
  let settleTimer;
  /** @type {MutationObserver | null} */
  let settleObserver = null;

  const getRoot = () => document.querySelector('[data-bp-group-by-category]');

  /**
   * Odkryje nadpis skupiny, jen když v mřížce nějaká její karta je.
   * @param {Element} grid
   */
  function syncHeadings(grid) {
    const present = new Set(
      Array.from(grid.querySelectorAll(CARD_SELECTOR))
        .map((card) => card.dataset.bpGroup)
        .filter((g) => g != null && g !== '')
    );

    let first = true;
    grid.querySelectorAll('[data-bp-group-heading]').forEach((heading) => {
      heading.hidden = !present.has(heading.dataset.bpGroupHeading);
      // Odsazení shora patří jen nadpisům MEZI skupinami — když filtr první
      // skupinu vyprázdní, nesmí nad mřížkou zůstat prázdné místo.
      heading.classList.toggle('product-grid__group-heading--first', !heading.hidden && first);
      if (!heading.hidden) first = false;
    });
  }

  /**
   * Adresa pro dotažení stránek. Bere se z odkazu ve stránkování, protože ten
   * nese aktuální filtr i řazení přesně tak, jak je vykreslil server —
   * `window.location` může být o krok napřed.
   * @param {Element} root
   * @returns {URL}
   */
  function baseUrlFor(root) {
    const link = root.querySelector('nav.pagination a[href]');
    const url = new URL(link ? link.getAttribute('href') : window.location.href, window.location.origin);
    url.searchParams.delete('page');
    return url;
  }

  /**
   * Číslo stránky, kterou vykreslil server.
   * @param {Element} root
   * @returns {number}
   */
  function currentPageOf(root) {
    const nav = root.querySelector('nav.pagination');
    const fromNav = parseInt(nav?.getAttribute('data-current_page') || '', 10);
    if (fromNav > 0) return fromNav;
    const fromUrl = parseInt(new URL(window.location.href).searchParams.get('page') || '', 10);
    return fromUrl > 0 ? fromUrl : 1;
  }

  /**
   * Dotáhne všechny ostatní stránky a poskládá mřížku v pořadí stránek.
   *
   * Nestačí připojovat na konec: zákazník může přijít rovnou na `?page=2`
   * (odkaz, historie prohlížeče), a pak by se prvních 36 barev objevilo AŽ ZA
   * těmi z druhé stránky — uvnitř skupiny by se rozsypala abeceda.
   *
   * @param {Element} root
   * @param {Element} grid
   * @param {AbortSignal} signal
   */
  async function loadAllPages(root, grid, signal) {
    const lastPage = parseInt(grid.dataset.lastPage || '1', 10);
    const sectionId = root.getAttribute('section-id');
    if (!sectionId || !(lastPage > 1)) return;

    const base = baseUrlFor(root);
    const current = currentPageOf(root);

    /** @type {Map<number, Element[]>} karty podle stránky, ze které pocházejí */
    const byPage = new Map();
    byPage.set(current, Array.from(grid.querySelectorAll(CARD_SELECTOR)));
    const seen = new Set(byPage.get(current).map((c) => c.dataset.productId));

    for (let page = 1; page <= Math.min(lastPage, MAX_PAGES); page++) {
      if (page === current) continue;

      const url = new URL(base.toString());
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('page', String(page));

      let html;
      try {
        const response = await fetch(url.toString(), { signal });
        if (!response.ok) continue;
        html = await response.text();
      } catch (e) {
        // AbortError = mezitím přišel nový filtr, tenhle běh už nikoho nezajímá.
        return;
      }
      if (signal.aborted || !grid.isConnected) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const cards = [];
      parsed.querySelectorAll(CARD_SELECTOR).forEach((card) => {
        if (seen.has(card.dataset.productId)) return;
        seen.add(card.dataset.productId);
        const clone = document.importNode(card, true);
        clone.setAttribute(APPENDED_ATTR, ''); // ať jde před dalším během spolehlivě uklidit
        cards.push(clone);
      });
      if (cards.length) byPage.set(page, cards);
    }

    if (signal.aborted || !grid.isConnected) return;

    const fragment = document.createDocumentFragment();
    Array.from(byPage.keys())
      .sort((a, b) => a - b)
      .forEach((page) => byPage.get(page).forEach((card) => fragment.appendChild(card)));
    grid.appendChild(fragment); // už vložené karty se jen přesunou na správné místo

    // Sentinel nekonečného scrollu už nemá co dotahovat — jinak by po scrollu
    // připojil stránky ještě jednou. Stránkovací lištu schovává CSS.
    root.querySelectorAll('[ref="viewMoreNext"], [ref="viewMorePrevious"]').forEach((el) => el.remove());
  }

  async function run() {
    const root = getRoot();
    if (!root) return;

    const grid = root.querySelector('.product-grid');
    if (!grid) return;

    inFlight?.abort();
    const controller = new AbortController();
    inFlight = controller;

    // Karty z předchozího běhu pryč DŘÍV, než se cokoli dotahuje — po změně
    // filtru v mřížce zůstávají a bez tohohle by se výsledky obou filtrů
    // míchaly dohromady.
    grid.querySelectorAll('[' + APPENDED_ATTR + ']').forEach((el) => el.remove());
    syncHeadings(grid);

    await loadAllPages(root, grid, controller.signal);

    if (controller.signal.aborted || !grid.isConnected) return;
    syncHeadings(grid);
    if (inFlight === controller) inFlight = null;
  }

  /**
   * Spustí `run()` až se mřížka po překreslení uklidní. Událost od filtru
   * chodí dřív, než téma sekci vymění, takže hned po ní by se pracovalo
   * se starým obsahem.
   */
  function scheduleAfterRerender() {
    inFlight?.abort();
    clearTimeout(settleTimer);
    settleObserver?.disconnect();

    const root = getRoot();
    const grid = root?.querySelector('.product-grid');
    if (!grid) return;

    const fire = () => {
      clearTimeout(settleTimer);
      settleObserver?.disconnect();
      settleObserver = null;
      run();
    };

    settleObserver = new MutationObserver(() => {
      clearTimeout(settleTimer);
      settleTimer = window.setTimeout(fire, SETTLE_MS);
    });
    settleObserver.observe(grid, { childList: true, subtree: false });

    // Když se mřížka nezmění vůbec (filtr dal stejný výsledek), ať to nevisí.
    settleTimer = window.setTimeout(fire, SETTLE_TIMEOUT_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => run(), { once: true });
  } else {
    run();
  }

  document.addEventListener('shopify:collection:update', scheduleAfterRerender);
  document.addEventListener('shopify:search:update', scheduleAfterRerender);
})();
